/**
 * The "Live NCRs Overdue" chart's drill-down: which metric a bar click opens
 * and which segment it lists. Shared by the Default tab and the Site KPI
 * quality panel so the two entry points cannot drift.
 *
 * CR-6676 (round 3): the chart buckets OPEN NCRs by the business `ncr_date`
 * and calls one overdue once its computed CA required date (raised date + the
 * customer's containment lead time in working days) has passed. Its own
 * drill-down metric, `ncr_live_overdue`, applies the same window, scope and
 * rule, so the modal lists exactly the bar's NCRs. The shared `ncr` metric
 * (raised-date window) is left to its other callers.
 */

/** Drill-down metric key for a Live NCRs Overdue bar (backend registry key). */
export const LIVE_NCRS_OVERDUE_METRIC = 'ncr_live_overdue'

/** The chart segment a drill lists: past the CA required date, or not yet. */
export type NcrLifecycle = 'overdue' | 'pending'

/** One Live NCRs Overdue bucket, as the dashboard payload carries it. */
export interface LiveNcrsPoint {
  /** The bucket label (e.g. "Jan 26") — also the clicked label. */
  month: string
  overdue?: number
  pending?: number
}

/**
 * The segment a click on a Live NCRs Overdue bucket opens.
 *
 * A click on a coloured segment names it (`segment`): the red Overdue part
 * lists the overdue NCRs, the amber Pending part the pending ones, so the
 * drill always matches the figure the tooltip shows for that segment. A click
 * elsewhere in the column cannot tell which segment was meant, so it opens the
 * overdue segment (the chart's headline) and falls back to pending for a
 * bucket with nothing overdue, so the drill still lists records.
 *
 * @param series - the chart's points (`charts.liveNCRsOverdue`)
 * @param month - the clicked bucket label
 * @param segment - the segment clicked, when the click landed on one
 * @returns the segment to list
 * @example liveNcrsLifecycle([{ month: 'Jan 26', overdue: 2, pending: 1 }], 'Jan 26') // 'overdue'
 * @example liveNcrsLifecycle([{ month: 'Jan 26', overdue: 2, pending: 1 }], 'Jan 26', 'pending') // 'pending'
 */
export function liveNcrsLifecycle(series: LiveNcrsPoint[] | undefined, month: string, segment?: NcrLifecycle): NcrLifecycle {
  if (segment) return segment
  const point = series?.find(d => d.month === month)
  return (point?.overdue ?? 0) > 0 ? 'overdue' : 'pending'
}

/**
 * The drill-down filters for a Live NCRs Overdue bucket: the chart's scope and
 * month window plus the clicked segment.
 *
 * @param chartFilters - the dashboard scope with the bucket's `date_from` / `date_to`
 * @param series - the chart's points (`charts.liveNCRsOverdue`)
 * @param month - the clicked bucket label
 * @param segment - the segment clicked, when the click landed on one
 * @returns `chartFilters` with `ncr_lifecycle` set (see {@link liveNcrsLifecycle})
 */
export function liveNcrsDrillFilters(
  chartFilters: Record<string, string>,
  series: LiveNcrsPoint[] | undefined,
  month: string,
  segment?: NcrLifecycle,
): Record<string, string> {
  return { ...chartFilters, ncr_lifecycle: liveNcrsLifecycle(series, month, segment) }
}

/** The part of a Recharts bar click this chart reads: the clicked point. */
interface LiveNcrsBarClick {
  payload?: { month?: unknown }
}

/**
 * A `<Bar onClick>` handler for one segment of the Live NCRs Overdue chart.
 *
 * Opens that segment's drill-down for the clicked bucket and stops the click
 * from also reaching the chart-level handler (which would open the default
 * segment on top of it).
 *
 * @param segment - the segment this `<Bar>` draws
 * @param open - opens the drill for `(month, segment)`
 * @returns the handler to pass as the `<Bar>`'s `onClick`
 * @example <Bar dataKey="pending" onClick={liveNcrsBarClick('pending', (m, s) => openFor(m, s))} />
 */
export function liveNcrsBarClick(
  segment: NcrLifecycle,
  open: (month: string, segment: NcrLifecycle) => void,
): (bar: LiveNcrsBarClick | undefined, index: number, event?: { stopPropagation?: () => void }) => void {
  return (bar, _index, event) => {
    event?.stopPropagation?.()
    const month = bar?.payload?.month
    if (typeof month === 'string' && month) open(month, segment)
  }
}
